import type { Location } from 'history';
import queryString from 'query-string';
import OnboardingStatus from '@renter-hub/constants/onboardingStatus';
import paths from '@renter-hub/pathsManifest';
import { VERIFICATION_STATUS } from '@common/constants/PlaidRequirement';

// TM-952: redirect renters whose bare /renter-hub/payments would render the
// legacy splash to Your Rental instead. Deep links (invite, /payment-info,
// etc.) are preserved. Mirrors the splash branches in HomePage.renderBody.
export const shouldRedirectToYourRental = ({
  onboardingStatus,
  onboardingComplete,
  paymentMethodStatus,
  location,
}: {
  onboardingStatus: string | undefined;
  onboardingComplete: boolean | undefined;
  paymentMethodStatus: string | undefined;
  location: Location | undefined;
}): boolean => {
  // Your Rental's "Go to payments" CTA links to `/renter-hub/payments/`,
  // but `paths.baseUrl` has no trailing slash — normalize before comparing.
  const pathname = location?.pathname ?? '';
  const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname;
  const isBarePaymentsPath = normalizedPathname === paths.baseUrl;
  const queryParams = queryString.parse(location?.search || '');
  const hasInviteToken = Boolean(queryParams.invite);

  if (!isBarePaymentsPath || hasInviteToken) {
    return false;
  }

  if (onboardingStatus === OnboardingStatus.REJECTED) {
    return !onboardingComplete;
  }

  if (
    onboardingStatus === OnboardingStatus.REGISTERED ||
    onboardingStatus === OnboardingStatus.NO_EXTERNAL_ACCOUNT_FOUND
  ) {
    // Pending Plaid verification falls through to bank-verification or the
    // dashboard in renderBody, not the splash — leave those renters alone.
    return (
      paymentMethodStatus !== VERIFICATION_STATUS.PENDING_AUTOMATIC.INTERNAL &&
      paymentMethodStatus !== VERIFICATION_STATUS.PENDING_MANUAL.INTERNAL
    );
  }

  return onboardingStatus === OnboardingStatus.NOT_STARTED;
};
